import { type SoundName, play } from 'cuelume';
import { useEffect, useCallback } from 'react';
import type { NavigateOptions } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';

type NavigateQueryOptions = NavigateOptions & {
  types?: string | string[];
  direction?: Direction;
  sound?: SoundName;
  remove?: boolean;
};

enum Direction {
  backward = -1,
  forward = 1,
  none = 0
}

export default (
  commonOptions: NavigateOptions = {
    preventScrollReset: false,
    viewTransition: false,
    relative: 'path',
    replace: false
  }
) => {
  let { search } = useLocation();
  const navigate = useNavigate();

  const ignoreNavigation = ({
    hasUAVisualTransition,
    downloadRequest,
    navigationType,
    canIntercept,
    hashChange,
    formData
  }: NavigateEvent) =>
    navigationType !== 'traverse' ||
    hasUAVisualTransition ||
    downloadRequest ||
    !canIntercept ||
    hashChange ||
    formData;

  const transitionOptions = CSS.supports('at-rule(@view-transition)');
  const { matches } = matchMedia('(prefers-reduced-motion: reduce)');

  const update = useCallback(
    (to: string | number, options?: NavigateQueryOptions) =>
      ~play(options?.sound ?? 'page') && typeof to === 'string'
        ? navigate(to, { ...commonOptions, ...options })
        : navigate(to as number),
    [navigate, commonOptions]
  );

  const transition = useCallback(
    (
      route: string | number,
      {
        types,
        ...options
      }: NavigateQueryOptions &
        Required<Pick<NavigateQueryOptions, 'direction'>>
    ) =>
      matches || !document.startViewTransition
        // No transition animation:
        ? update(route, options)
        : !transitionOptions
          // Obsolete syntax, only in Safari < 18.2:
          ? document.startViewTransition(() => update(route))
          : document.startViewTransition({
              update: () => update(route, options),
              types: types
                ? (Array.isArray(types) && types) || [types]
                : [
                    (options.direction === Direction.forward && 'right') ||
                      'left'
                  ]
            }),
    [matches, update, transitionOptions]
  );

  const onNavigate = useCallback(
    (event: NavigateEvent) => {
      if (ignoreNavigation(event)) return;
      const { url } = event.destination;

      const {
        href: { length },
        origin: prev
      } = location;

      const { origin, href, pathname } = new URL(url);
      const direction = Math.sign(href.length - length);

      prev === origin &&
        event.intercept({
          handler: () =>
            (!direction
              ? update(pathname)
              : transition(pathname, { direction })) && void 0
        });
    },
    [update, transition]
  );

  useEffect(() => {
    window.navigation?.addEventListener('navigate', onNavigate);
    return () => window.navigation?.removeEventListener('navigate', onNavigate);
  }, [onNavigate]);

  return (
    route: string | number,
    navigationOptions?: NavigateQueryOptions | number | boolean
  ) => {
    const options = navigationOptions as NavigateQueryOptions | undefined;
    const remove = typeof options === 'boolean' ? options : options?.remove;
    let direction = typeof options === 'number' ? options : options?.direction;

    if (typeof route === 'number')
      return transition(route, {
        direction: Math.sign(route),
        ...options
      });

    const queryIndex = route.indexOf('?');

    if (search.includes('?') && queryIndex > -1 && !remove) {
      search += `&${route.slice(queryIndex + 1)}`;
      route = route.slice(0, queryIndex);
    }

    // When transition direction is not defined, perform a best guess:
    // use slide left for the home page (/) and the right one for others.
    const to = `${route}${(!remove && search) || ''}`;
    direction ??= (to.length === 1 && -1) || 1;

    return !direction
      ? update(to, options)
      : transition(to, { direction, ...options });
  };
};
